import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  Box,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  AccountBalanceWallet as WalletIcon,
  AccountBalance as BankIcon,
  CheckCircle as VerifiedIcon,
  Cancel as UnverifiedIcon,
  Schedule as TimeIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { GamerResult } from "../../Model/gamer";

const GamerDetailsFetcher: React.FC<{ gamerUid: string }> = ({ gamerUid }) => {
  const [gamerData, setGamerData] = useState<GamerResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gamerUid) return;

    const fetchGamerDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://backend-finance-990423892517.africa-south1.run.app/gamerdetails/${gamerUid}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }

        const data = await response.json();
        setGamerData(data?.gamer_result || data);
      } catch (err: any) {
        setError(err?.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchGamerDetails();
  }, [gamerUid]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 700, margin: "auto", mt: 4 }}>
        <Alert severity="error">Unable to load gamer details: {error}</Alert>
      </Box>
    );
  }

  if (!gamerData) {
    return (
      <Box sx={{ maxWidth: 700, margin: "auto", mt: 4 }}>
        <Alert severity="info">
          No details found for gamer UID: {gamerUid}
        </Alert>
      </Box>
    );
  }

  const {
    timestamp,
    credit_balance,
    nickname,
    uid,
    phonenumber,
    banking,
    details,
  } = gamerData;

  const formattedDate = timestamp
    ? new Date(timestamp).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

  const creditVal =
    typeof credit_balance === "number"
      ? credit_balance
      : parseFloat(credit_balance || "0");

  return (
    <Box sx={{ maxWidth: 700, margin: "auto", p: 2 }}>
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
              {nickname ? nickname.charAt(0).toUpperCase() : "U"}
            </Avatar>
          }
          title={
            <Typography variant="h5" component="div" fontWeight="bold">
              {nickname || "Unknown Gamer"}
            </Typography>
          }
          subheader={`UID: ${uid}`}
          action={
            <Chip
              icon={<WalletIcon />}
              label={`R${creditVal.toFixed(2)}`}
              color="primary"
              variant="filled"
              sx={{ fontWeight: "bold", fontSize: "1rem", p: 1 }}
            />
          }
        />

        <Divider />

        <CardContent>
          <Grid container spacing={3}>
            {/* Quick Summary */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Account Summary
              </Typography>

              <Box display="flex" gap={3} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {details?.phonenumber || "N/A"}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <TimeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Joined: {formattedDate}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Personal Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mt: 1, mb: 1.5 }}
              >
                Personal Details
              </Typography>

              {details ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PersonIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          <strong>Full Name:</strong> {details.firstName}{" "}
                          {details.lastName}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BadgeIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          <strong>ID Number:</strong>{" "}
                          {`${details.idNumber.slice(0, 6)}********` || "N/A"}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <HomeIcon
                          color="primary"
                          fontSize="small"
                          sx={{ mt: 0.3 }}
                        />
                        <Typography variant="body2">
                          <strong>Address:</strong> {details.address || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      flexDirection="column"
                      gap={1.5}
                      justifyContent="center"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          ID Verification:
                        </Typography>
                        {details.IdNumberVerified ? (
                          <Chip
                            icon={<VerifiedIcon />}
                            label="Verified"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<UnverifiedIcon />}
                            label="Unverified"
                            color="warning"
                            size="small"
                          />
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Address Verification:
                        </Typography>
                        {details.AddressVerified ? (
                          <Chip
                            icon={<VerifiedIcon />}
                            label="Verified"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<UnverifiedIcon />}
                            label="Unverified"
                            color="warning"
                            size="small"
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No personal details provided.
                </Typography>
              )}
            </Grid>

            {/* Banking Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mt: 1, mb: 1.5 }}
              >
                Banking Details
              </Typography>

              {banking ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BankIcon color="primary" />
                        <Typography variant="subtitle2" fontWeight="bold">
                          {banking.bank} ({banking.accountType} Account)
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>Account Holder:</strong> {banking.accountHolder}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Account Number:</strong> {banking.accountNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Branch Code:</strong> {banking.branchCode}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      flexDirection="column"
                      gap={1.5}
                      justifyContent="center"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Verification Status:
                        </Typography>
                        {banking.isVerified ? (
                          <Chip
                            icon={<VerifiedIcon />}
                            label="Verified"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<UnverifiedIcon />}
                            label="Unverified"
                            color="warning"
                            size="small"
                          />
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Approval Status:
                        </Typography>
                        {banking.isAccepted ? (
                          <Chip label="Accepted" color="success" size="small" />
                        ) : (
                          <Chip
                            label="Pending / Rejected"
                            color="default"
                            size="small"
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No banking details linked.
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GamerDetailsFetcher;
